// ClientHome — müşteri karşılama ekranı (Müşteri Katılımcı Rolü, Task 8).
// Onaylı görsel spec + 3 durum: design-ref.md "Onaylı görsel spec — ClientHome 3
// durum" + mockup ~/.gstack/projects/BT-Akademi-mahir-ai/designs/client-home-20260723/
// client-home-sketch.html (DOM yapısı + copy birebir referans; renk token değerleri
// mockup'taki hardcoded hex DEĞİL, canlı var(--color-*) — bkz. globals.css v2 palette).
//
// Veri sözleşmesi notları (dar client DTO, services/projects.ts listProjectsForClient):
// - GET /projects client'a SADECE {id, frameworkName, reportingYear, participantStatus}
//   döner, filtre status IN ('invited','active') — submitted/revoked projeler LİSTEDE
//   HİÇ YOK. Bu yüzden "Gönderildi" durumu backend'den yeniden sorgulanamaz; FormFill
//   submit anında lib/client-submission.ts ile localStorage'a yazar, burada okunur.
// - "Firma adı" (danışmanlık firması) hiçbir client-whitelist endpoint'inde YOK
//   (mail.from_name system_settings'te, branding endpoint'leri whitelist dışı) →
//   copy'ler jenerik "danışmanınız" ile yazıldı, mockup'taki {firma} yer tutucusu
//   doldurulmadı (devir — task-8-report.md "Firma adı" bölümü).
// - Sıralama ("davet tarihine göre en yeni üstte") backend'de zaten desc(createdAt) —
//   FE ekstra sort YAPMAZ.
// - M16 T19 (DR2/RK14): form katılımı YOK + set erişimi VAR → /portal/kpi/tablo; set sorgusu hata
//   verirse form dalına düşülür (fail-open). Tek-proje auto-redirect ve diğer durumlar DEĞİŞMEDİ.
//   İkisi de yoksa boş durum: "Henüz size açılmış bir çalışma yok — şirket yöneticinize başvurun".
// - Portal turu (2026-10-06): kök landmark `<main>` (kabuk artık çizmez — P3-26); "Formlarım"
//   sekmesine bilinçli tıklama (`location.state.explicit`) form yokken KPI'ya geri sıçramaz —
//   boş durum gösterilir (P3-14); boş durumda iki mesaj tek paragrafta birleşmez (P4-28).
// - K8 (2026-10-23): çok firmalı müşteride proje listesi portal-store aktif firmasına süzülür ve
//   firma değişince yeniden çekilir; landing (DR2) böylece aktif firmanın formu/seti üzerinden.

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  formatReportingPeriod,
  type ClientProjectsListResponse,
  type ClientProjectView,
} from "@mahir/shared";
import { api } from "../api/client";
import { useAuthStore } from "../stores/auth-store";
import { usePortalKpiStore } from "../stores/portal-kpi-store";
import { usePortalStore } from "../stores/portal-store";
import { getLastClientSubmission, type LastClientSubmission } from "../lib/client-submission";
import { KPI_COPY } from "../lib/kpi-ui";
import { decideClientLanding } from "../lib/portal-kpi-ui";

type LoadState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; projects: ClientProjectView[] };

const CONTENT_STYLE: CSSProperties = {
  padding: "40px 48px 56px",
  maxWidth: 760,
  fontFamily: "var(--font-sans)",
};

export function ClientHome(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [kickoutMessage, setKickoutMessage] = useState<string | null>(
    (location.state as { kickoutMessage?: string } | null)?.kickoutMessage ?? null,
  );
  const setsStatus = usePortalKpiStore((s) => s.setsStatus);
  const setCount = usePortalKpiStore((s) => s.sets.length);
  // K8 — çok firmalı müşteri: liste aktif firmaya süzülür; firma listesi yoksa süzme yok.
  const activeClientId = usePortalStore((s) =>
    s.firmsStatus === "ready" ? s.activeClientId : null,
  );
  // Firma listesi bitmeden çekilen süzülmemiş liste tek-proje yönlendirmesini yanıltırdı.
  const firmsSettled = usePortalStore(
    (s) => s.firmsStatus === "ready" || s.firmsStatus === "error",
  );
  // P3-14 — sekmeden gelindi (explicit): KPI'ya otomatik yönlendirme yok
  const explicit = (location.state as { explicit?: boolean } | null)?.explicit === true;

  const fetchSeq = useRef(0);
  async function fetchProjects(): Promise<void> {
    const seq = ++fetchSeq.current;
    setState({ status: "loading" });
    try {
      const res = await api<ClientProjectsListResponse>("/projects");
      if (seq !== fetchSeq.current) return; // firma değişti — bayat yanıt atılır
      const projects = activeClientId
        ? res.projects.filter((p) => p.clientId === activeClientId)
        : res.projects;
      setState({ status: "ready", projects });
    } catch {
      if (seq === fetchSeq.current) setState({ status: "error" });
    }
  }

  useEffect(() => {
    if (firmsSettled) void fetchProjects();
    // firma değişince form listesi yeniden çekilir (K8)
  }, [activeClientId, firmsSettled]);

  // Tek aktif/davetli proje → otomatik forma yönlendirme (onaylı görsel spec).
  useEffect(() => {
    if (state.status === "ready" && state.projects.length === 1) {
      void navigate(`/projects/${state.projects[0]!.id}/form`, { replace: true });
    }
  }, [state, navigate]);

  // DR2 — form yok + set erişimi var → portal Veri Girişi (set sorgusu bitene kadar bekle).
  const landing =
    state.status === "ready" && (setsStatus === "ready" || setsStatus === "error")
      ? decideClientLanding({ projectCount: state.projects.length, setCount, setsStatus })
      : null;
  useEffect(() => {
    if (landing === "kpi" && !kickoutMessage && !explicit) {
      void navigate("/portal/kpi/tablo", { replace: true });
    }
  }, [landing, kickoutMessage, explicit, navigate]);

  const goToForm = (projectId: string): void => {
    void navigate(`/projects/${projectId}/form`);
  };

  return (
    <main style={CONTENT_STYLE}>
      {kickoutMessage && (
        <div
          role="alert"
          className="mb-6 flex items-start justify-between gap-3"
          style={{
            padding: "10px 14px",
            borderRadius: 8,
            background: "color-mix(in srgb, var(--color-error) 10%, white)",
            border: "1px solid color-mix(in srgb, var(--color-error) 35%, white)",
            color: "var(--color-error)",
            fontSize: "0.85rem",
          }}
        >
          <span>{kickoutMessage}</span>
          <button
            onClick={() => setKickoutMessage(null)}
            aria-label="Kapat"
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              cursor: "pointer",
            }}
          >
            ×
          </button>
        </div>
      )}

      {state.status === "loading" && <LoadingSkeleton />}
      {state.status === "error" && <ErrorState onRetry={() => void fetchProjects()} />}
      {state.status === "ready" && state.projects.length > 1 && (
        <ActiveState
          fullName={user?.fullName ?? ""}
          projects={state.projects}
          onOpenProject={goToForm}
        />
      )}
      {state.status === "ready" && state.projects.length === 1 && (
        <p style={{ color: "var(--color-text-2)" }}>Forma yönlendiriliyorsunuz…</p>
      )}
      {state.status === "ready" && state.projects.length === 0 && landing === null && (
        <LoadingSkeleton />
      )}
      {state.status === "ready" &&
        state.projects.length === 0 &&
        landing === "kpi" &&
        !explicit && (
          <p style={{ color: "var(--color-text-2)" }}>
            Performans göstergelerine yönlendiriliyorsunuz…
          </p>
        )}
      {state.status === "ready" && state.projects.length === 0 && landing === "kpi" && explicit && (
        <EmptyOrSubmittedState userId={user?.id ?? null} hasKpi />
      )}
      {state.status === "ready" && state.projects.length === 0 && landing === "none" && (
        <EmptyOrSubmittedState userId={user?.id ?? null} hasKpi={false} />
      )}
    </main>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <div aria-busy="true" aria-label="Yükleniyor">
      <div
        className="animate-pulse mb-3"
        style={{ height: 30, width: "60%", borderRadius: 6, background: "var(--color-border)" }}
      />
      <div
        className="animate-pulse mb-7"
        style={{ height: 16, width: "90%", borderRadius: 6, background: "var(--color-border)" }}
      />
      <div
        className="animate-pulse"
        style={{
          height: 130,
          maxWidth: 560,
          borderRadius: 8,
          background: "var(--color-border)",
          opacity: 0.5,
        }}
      />
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }): JSX.Element {
  return (
    <div>
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "1.5rem",
          color: "var(--color-text)",
          marginBottom: 10,
        }}
      >
        Yüklenemedi
      </h1>
      <p style={{ color: "var(--color-text-2)", marginBottom: 16 }}>
        Projeleriniz yüklenirken bir sorun oluştu.
      </p>
      <button
        onClick={onRetry}
        className="rounded-lg px-4 py-2 text-sm font-semibold"
        style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
      >
        Tekrar dene
      </button>
    </div>
  );
}

function ActiveState({
  fullName,
  projects,
  onOpenProject,
}: {
  fullName: string;
  projects: ClientProjectView[];
  onOpenProject: (projectId: string) => void;
}): JSX.Element {
  const plural = projects.length > 1;
  return (
    <div>
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 600,
          fontSize: "1.9rem",
          color: "var(--color-text)",
          marginBottom: 10,
        }}
      >
        Hoş geldiniz, {fullName}
      </h1>
      <p
        style={{
          color: "var(--color-text-2)",
          fontSize: "0.97rem",
          lineHeight: 1.55,
          marginBottom: 28,
          maxWidth: 560,
        }}
      >
        Danışmanınız, aşağıdaki {plural ? "projelerin" : "projenin"} form yanıtlarını doldurmanız
        için sizi davet etti. Yanıtlarınız otomatik kaydedilir — dilediğiniz zaman kaldığınız yerden
        devam edebilirsiniz.
      </p>
      <div className="flex flex-col gap-4">
        {projects.map((p) => (
          <ProjectCard key={p.id} project={p} onOpen={() => onOpenProject(p.id)} />
        ))}
      </div>
      <p style={{ marginTop: 18, fontSize: "0.82rem", color: "var(--color-text-3)" }}>
        Formu tamamlayınca &quot;Formu Tamamla ve Gönder&quot; ile danışmanınıza iletebilirsiniz.
      </p>
    </div>
  );
}

function ProjectCard({
  project,
  onOpen,
}: {
  project: ClientProjectView;
  onOpen: () => void;
}): JSX.Element {
  return (
    <div
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        borderRadius: 8,
        padding: "20px 22px",
        maxWidth: 560,
      }}
    >
      {/* 2026-09-30 — aynı kişi birden fazla müşteri firmaya davet edilebildiği için
          kartta hangi şirket adına doldurulduğu yazılır (kicker kalıbı). */}
      <div
        style={{
          fontSize: "0.72rem",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--color-text-3)",
          marginBottom: 6,
        }}
      >
        {project.clientName}
      </div>
      <div className="flex items-baseline justify-between gap-3" style={{ marginBottom: 14 }}>
        <span
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.15rem",
            color: "var(--color-text)",
          }}
        >
          {project.frameworkName}
        </span>
        <span style={{ fontSize: "0.8rem", color: "var(--color-text-3)" }}>
          {formatReportingPeriod(project.reportingYear, project.periodType)}
        </span>
      </div>
      <button
        onClick={onOpen}
        className="rounded-lg px-5 py-2.5 text-sm font-semibold"
        style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
      >
        Forma Git →
      </button>
    </div>
  );
}

function EmptyOrSubmittedState({
  userId,
  hasKpi,
}: {
  userId: string | null;
  hasKpi: boolean;
}): JSX.Element {
  // Fix Turu 1 (IMPORTANT-1) — cache kullanıcıya göre namespace'li; userId yoksa
  // (teorik olarak client route'a auth'suz erişim yok ama savunmacı) okuma yapma.
  const last = userId ? getLastClientSubmission(userId) : null;
  if (last) return <SubmittedState submission={last} />;

  return (
    <div>
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 600,
          fontSize: "1.7rem",
          color: "var(--color-text)",
          marginBottom: 10,
        }}
      >
        {KPI_COPY.portal.noWorkTitle}
      </h1>
      <p
        style={{
          color: "var(--color-text-2)",
          fontSize: "0.95rem",
          lineHeight: 1.55,
          maxWidth: 560,
        }}
      >
        {KPI_COPY.portal.noFormsBody}
      </p>
      {!hasKpi ? (
        <p style={{ color: "var(--color-text-3)", fontSize: "0.88rem", marginTop: 10 }}>
          {KPI_COPY.portal.noWorkBody}
        </p>
      ) : null}
    </div>
  );
}

function SubmittedState({ submission }: { submission: LastClientSubmission }): JSX.Element {
  const dateLabel = new Date(submission.submittedAt).toLocaleDateString("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return (
    <div>
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: "50%",
          background: "color-mix(in srgb, var(--color-success) 16%, white)",
          color: "var(--color-success)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.4rem",
          marginBottom: 16,
        }}
        aria-hidden="true"
      >
        ✓
      </div>
      <h1
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 600,
          fontSize: "1.7rem",
          color: "var(--color-text)",
          marginBottom: 10,
        }}
      >
        Katkınız için teşekkürler
      </h1>
      <p
        style={{
          color: "var(--color-text-2)",
          fontSize: "0.95rem",
          lineHeight: 1.55,
          maxWidth: 560,
          marginBottom: 20,
        }}
      >
        Form yanıtlarınız danışmanınıza iletildi. Süreç danışmanınız tarafından yürütülecek; ek
        bilgi gerekirse formu sizin için yeniden açabilir — bu durumda size e-posta ile haber
        verilir.
      </p>
      <div
        style={{
          background: "var(--color-surface)",
          border: "1px dashed var(--color-border)",
          borderRadius: 8,
          padding: 20,
          maxWidth: 560,
          color: "var(--color-text-2)",
          fontSize: "0.88rem",
        }}
      >
        {submission.frameworkName} ·{" "}
        {formatReportingPeriod(submission.reportingYear, submission.periodType)} —{" "}
        <strong>{dateLabel}</strong> tarihinde gönderildi.
      </div>
    </div>
  );
}
